import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, CheckCircle2, FileText, Upload, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import SignaturePad from '../shared/SignaturePad';

const SaleAuthorizationSignDialog = ({ open, onClose, deliverableId, onSigned }) => {
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [signaturePng, setSignaturePng] = useState(null);
  const [signatoryName, setSignatoryName] = useState('');
  const [signDate, setSignDate] = useState(new Date().toISOString().split('T')[0]);
  const [deliverable, setDeliverable] = useState(null);
  const [sigMode, setSigMode] = useState('draw'); // 'draw' or 'upload'
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && deliverableId) loadData();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [open, deliverableId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dr = await fetch(`${window.location.origin}/api/sale-owner-deliverables/${deliverableId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      if (dr && dr.ok) {
        const d = await dr.json();
        setDeliverable(d);
        if (d.authorization_file_id) {
          const fr = await fetch(`${window.location.origin}/api/files/download/${d.authorization_file_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (fr.ok) {
            const blob = await fr.blob();
            setPreviewUrl(URL.createObjectURL(blob));
          }
        }
      }
    } catch (e) { console.error('Failed to load authorization:', e); }
    finally { setLoading(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, etc.)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setSignaturePng(dataUrl);
      setUploadPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleModeSwitch = (mode) => {
    setSigMode(mode);
    setSignaturePng(null);
    setUploadPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSign = async () => {
    if (!signaturePng && !signatoryName.trim()) { toast.error('Please draw/upload your signature or enter your name'); return; }
    setSigning(true);
    try {
      await api.post(`/sale-authorizations/sign/${deliverableId}`, {
        signature_png: signaturePng,
        signatory_name: signatoryName,
        date: new Date(signDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      });
      toast.success('Authorization executed successfully');
      onSigned?.();
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sign authorization');
    } finally { setSigning(false); }
  };

  const hasSignature = Boolean(signaturePng) || Boolean(signatoryName.trim());

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl flex flex-col max-h-[90vh] gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Execute Authorization
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : (
            <>
              {/* PDF Preview */}
              {previewUrl ? (
                <div className="border rounded bg-gray-50">
                  <p className="text-xs text-muted-foreground px-3 pt-2 pb-1">Authorization Document</p>
                  <object data={previewUrl} type="application/pdf" className="w-full rounded" style={{ height: 320 }}>
                    <p className="text-sm text-muted-foreground p-4">Unable to preview PDF in browser.</p>
                  </object>
                </div>
              ) : (
                <div className="border rounded p-8 text-center text-muted-foreground bg-gray-50">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Authorization document not available for preview</p>
                </div>
              )}

              {/* Signature Block */}
              <div className="border rounded p-4 space-y-4 bg-muted/10">
                <p className="text-sm font-semibold">Signature</p>
                <p className="text-xs text-muted-foreground">
                  By signing below you confirm that you are authorized to execute this agreement on behalf of the owner.
                </p>

                {/* Mode toggle: Draw / Upload */}
                <div className="flex gap-1 bg-muted rounded-md p-0.5 w-fit" data-testid="signature-mode-toggle">
                  <button
                    type="button"
                    onClick={() => handleModeSwitch('draw')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${sigMode === 'draw' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="signature-mode-draw"
                    disabled={signing}
                  >
                    <PenLine className="w-3.5 h-3.5" /> Draw
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeSwitch('upload')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${sigMode === 'upload' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid="signature-mode-upload"
                    disabled={signing}
                  >
                    <Upload className="w-3.5 h-3.5" /> Upload
                  </button>
                </div>

                {/* Draw mode */}
                {sigMode === 'draw' && (
                  <div>
                    <Label className="text-xs mb-2 block">Draw your signature *</Label>
                    <SignaturePad onChange={setSignaturePng} disabled={signing} />
                  </div>
                )}

                {/* Upload mode */}
                {sigMode === 'upload' && (
                  <div className="space-y-2">
                    <Label className="text-xs mb-2 block">Upload signature image *</Label>
                    <div
                      className="border-2 border-dashed border-gray-300 rounded bg-white p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="signature-upload-area"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={signing}
                        data-testid="signature-file-input"
                      />
                      {uploadPreview ? (
                        <div className="space-y-2">
                          <img src={uploadPreview} alt="Uploaded signature" className="max-h-[80px] mx-auto" />
                          <p className="text-xs text-muted-foreground">Click to replace</p>
                        </div>
                      ) : (
                        <div className="py-2">
                          <Upload className="w-6 h-6 mx-auto mb-1.5 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Click to upload a signature image</p>
                          <p className="text-xs text-muted-foreground mt-0.5">PNG, JPG or other image formats</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Print Name (or type for typed signature)</Label>
                    <Input value={signatoryName} onChange={e => setSignatoryName(e.target.value)} placeholder="Full name" disabled={signing} data-testid="signatory-name-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date *</Label>
                    <Input type="date" value={signDate} onChange={e => setSignDate(e.target.value)} className="h-9 text-sm" disabled={signing} data-testid="sign-date-input" />
                  </div>
                </div>

                <p className="text-xs text-muted-foreground italic">
                  By submitting this form you are providing a legally binding electronic signature.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={() => onClose(false)} disabled={signing} data-testid="sign-cancel-btn">Cancel</Button>
          <Button onClick={handleSign} disabled={signing || loading || !hasSignature} className="bg-primary hover:bg-primary/90 text-white" data-testid="sign-execute-btn">
            {signing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Executing...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Execute & Sign</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaleAuthorizationSignDialog;
